import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleEdgePoints, sampleLineSegments } from '../pointCloudUtils';
import { BUILDING_PRIMARY, BUILDING_WINDOW, BUILDING_DIVIDER } from '../twinColors';

/**
 * BuildingLoad - Point cloud multi-story building with window glow grid,
 * platform base, parapet walls, and rooftop HVAC units.
 * Window glow scales with load and boosts at night.
 */
export default function BuildingLoad({ state, position, onClick, isNight }) {
    const windowRefs = useRef([]);

    const loadRatio = useMemo(() => {
        const peak = state?.peak_kw || 500;
        if (!state?.power_kw) return 0;
        return Math.min(1, Math.max(0, state.power_kw / peak));
    }, [state?.power_kw, state?.peak_kw]);

    // Main building edge points (dispose intermediate geometries)
    const buildingPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(12, 10, 8);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 800, 0.08);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    // Platform base edge points
    const platformPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(14, 0.3, 10);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 200, 0.06);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    // Parapet edge points
    const parapetPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(12.4, 0.5, 8.4);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 150, 0.05);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    // HVAC unit edge points
    const hvacPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(1.5, 0.8, 1);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 60, 0.04);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    // Floor separator particles
    const floorSepPoints = useMemo(() => {
        const positions = [];
        for (const y of [3.3, 6.6]) {
            positions.push(-6, y, 4, 6, y, 4);
            positions.push(-6, y, -4, 6, y, -4);
            positions.push(-6, y, -4, -6, y, 4);
            positions.push(6, y, -4, 6, y, 4);
        }
        return sampleLineSegments(new Float32Array(positions), 120, 0.05);
    }, []);

    // Window positions
    const windows = useMemo(() => {
        const items = [];
        let idx = 0;
        const floorYs = [1.65, 4.95, 8.25];

        // Front face
        for (let f = 0; f < 3; f++) {
            for (let c = 0; c < 4; c++) {
                const x = (c - 1.5) * 2.4;
                items.push({ pos: [x, floorYs[f], 4.01], rot: [0, 0, 0], idx: idx++ });
            }
        }

        // Right face
        for (let f = 0; f < 3; f++) {
            for (let c = 0; c < 4; c++) {
                const z = (c - 1.5) * 1.6;
                items.push({ pos: [6.01, floorYs[f], z], rot: [0, Math.PI / 2, 0], idx: idx++ });
            }
        }

        return items;
    }, []);

    useFrame(({ clock }) => {
        const t = clock.elapsedTime;
        for (let i = 0; i < windowRefs.current.length; i++) {
            const ref = windowRefs.current[i];
            if (ref) {
                const base = 0.1 + loadRatio * 0.3;
                const nightMult = isNight ? 2.0 : 1.0;
                const flicker = Math.sin(t * (2 + i * 0.3)) * 0.02;
                ref.material.opacity = Math.max(0.01, (base + flicker) * nightMult);
            }
        }
    });

    return (
        <group position={position} onClick={onClick}>
            {/* Main building particles */}
            <points position={[0, 5, 0]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={buildingPoints.length / 3}
                        array={buildingPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={BUILDING_PRIMARY}
                    size={0.1}
                    transparent
                    opacity={0.4}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Platform base particles */}
            <points position={[0, 0.15, 0]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={platformPoints.length / 3}
                        array={platformPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={BUILDING_PRIMARY}
                    size={0.08}
                    transparent
                    opacity={0.3}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Floor separator particles */}
            <points>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={floorSepPoints.length / 3}
                        array={floorSepPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={BUILDING_DIVIDER}
                    size={0.06}
                    transparent
                    opacity={0.2}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Parapet particles */}
            <points position={[0, 10.25, 0]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={parapetPoints.length / 3}
                        array={parapetPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={BUILDING_PRIMARY}
                    size={0.08}
                    transparent
                    opacity={0.35}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* HVAC units */}
            <points position={[-3, 10.4, -2]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={hvacPoints.length / 3}
                        array={hvacPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={BUILDING_PRIMARY}
                    size={0.06}
                    transparent
                    opacity={0.3}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>
            <points position={[2, 10.4, 1]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={hvacPoints.length / 3}
                        array={hvacPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={BUILDING_PRIMARY}
                    size={0.06}
                    transparent
                    opacity={0.3}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Window glow planes (additive blue) */}
            {windows.map(({ pos, rot, idx: wi }) => (
                <mesh
                    key={`win-${wi}`}
                    ref={(el) => { windowRefs.current[wi] = el; }}
                    position={pos}
                    rotation={rot}
                >
                    <planeGeometry args={[0.8, 1.2]} />
                    <meshBasicMaterial
                        color={BUILDING_WINDOW}
                        transparent
                        opacity={0.02}
                        depthWrite={false}
                        blending={THREE.AdditiveBlending}
                    />
                </mesh>
            ))}
        </group>
    );
}
